// ============================================================================
// VendorInventoryRows — what we already hold of a variant, per warehouse.
//
// Extracted from the procurement cart so /oms/availability renders the SAME
// rows from the SAME payload. Both screens read `vendor_inventory` off their
// line (shaped by ecom_module.serializers.CartItemDetailsFullSerializers), so
// the two desks can never disagree about what stock exists.
//
// `incomingRows` (the line's `incoming_purchase_orders`, same serializer) adds
// what is still ON ORDER: one row per outstanding Zoho stock-PO line, with the
// PO number and delivery date, under the stock rows. A row stays uncoloured
// while its delivery date is ahead and turns light red once it has passed.
// ============================================================================
import { ClockCircleOutlined, FileTextOutlined, GoldOutlined } from "@ant-design/icons";
import {
  Button, Space, Switch, Table, Tag, Tooltip, Typography, message } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";

import { setAutoFillAllowed } from "../../apis/procurement.api";
import { ZOHO_PO_URL } from "./incoming_stock_table";

/**
 * "Auto-fill allowed" — the variant's place on the sellable list.
 *
 * VendorInventory mirrors everything Zoho and Uro Veneer hold, but the cart
 * only PROMISES stock for variants on that list. The desks see all stock and
 * flip this to decide what may auto-fill. Backend: SellableInventoryVariant.
 */
export function AutoFillToggle({
  variantId,
  allowed,
  onChange,
}: {
  variantId: number | null | undefined;
  allowed: boolean | null | undefined;
  onChange?: (allowed: boolean) => void;
}) {
  const [value, setValue] = useState<boolean>(!!allowed);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(!!allowed), [allowed, variantId]);
  if (!variantId) return null;
  return (
    <Space size={8} align="center">
      <Switch
        size="small"
        checked={value}
        loading={busy}
        onChange={async (next) => {
          setBusy(true);
          try {
            await setAutoFillAllowed(variantId, next);
            setValue(next);
            onChange?.(next);
            message.success(next ? "Auto-fill enabled for this product" : "Auto-fill disabled for this product");
          } catch (e: any) {
            message.error(e?.response?.data?.error?.message || "Could not update auto-fill");
          } finally {
            setBusy(false);
          }
        }}
      />
      <Tooltip title="When on, the cart fills lead time 0 from this stock automatically (same-zone, single warehouse). When off, the stock is only shown here and the line goes to a vendor or the desk.">
        <span style={{ fontSize: 12, color: value ? "#389e0d" : "#8c8c8c", fontWeight: 600 }}>
          Auto-fill {value ? "allowed" : "off"}
        </span>
      </Tooltip>
    </Space>
  );
}

export const ACCENT = "#24AAB7";

// Row colouring: stock the cart may PROMISE (variant on the sellable list) vs
// stock we merely hold. Both are shown; only the first auto-fills.
const ALLOWED = { bg: "#f6ffed", border: "#52c41a", fg: "#389e0d", label: "Auto-fill allowed" };
const BLOCKED = { bg: "#fffbe6", border: "#faad14", fg: "#ad6800", label: "Auto-fill off" };
export function rowTone(inv: any) {
  return inv?.auto_fill_allowed ? ALLOWED : BLOCKED;
}

export function formatQty(value: any) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0";
  // Trim trailing zeros from the DecimalField (e.g. 12.00 -> 12, 12.50 -> 12.5).
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function stockStatus(value: any) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) {
    return { label: "Out of stock", color: "#cf1322", dot: "#cf1322" };
  }
  if (n < 10) {
    return { label: "Low stock", color: "#d46b08", dot: "#fa8c16" };
  }
  return { label: "In stock", color: "#389e0d", dot: "#52c41a" };
}

// Incoming PO rows: no tone while the delivery date is ahead (or missing);
// light red once it has passed. Backend decides `is_overdue` on its local
// date so every client colours the same rows.
const INCOMING_OVERDUE = { bg: "#fff1f0", border: "#ffa39e", fg: "#cf1322" };

type PanelRow = Record<string, any> & { __kind: "stock" | "incoming_po"; __key: string };

const isIncoming = (row: PanelRow) => row.__kind === "incoming_po";

/** "DD MMM YYYY" plus how late / how soon; "Not set" when the PO has no date. */
export function IncomingDeliveryDate({ row }: { row: any }) {
  const date = row?.delivery_date;
  if (!date) {
    return (
      <Tooltip title="No delivery date on this PO in Zoho">
        <span style={{ color: "#8c8c8c", fontSize: 12 }}>Not set</span>
      </Tooltip>
    );
  }
  const days = moment(date).startOf("day").diff(moment().startOf("day"), "days");
  const overdue = row?.is_overdue ?? days < 0;
  const hint = overdue
    ? `${Math.abs(days)}d overdue`
    : days === 0
    ? "due today"
    : `in ${days}d`;
  return (
    <Tooltip title={`Delivery date on the PO · ${hint}`}>
      <span
        style={{
          color: overdue ? INCOMING_OVERDUE.fg : "#595959",
          fontSize: 12,
          fontWeight: overdue ? 600 : 400,
        }}
      >
        {moment(date).format("DD MMM YYYY")}
        <span style={{ marginLeft: 4, fontWeight: 400, color: overdue ? INCOMING_OVERDUE.fg : "#8c8c8c" }}>
          · {hint}
        </span>
      </span>
    </Tooltip>
  );
}

export function VendorInventoryRows({
  rows,
  incomingRows = [],
  showEmpty = false,
  variantId,
  onAutoFillChange,
  selectedIncomingPoId,
  onPickIncomingPo,
}: {
  rows: Array<any>;
  /**
   * Outstanding Zoho stock-PO lines for the variant (`incoming_purchase_orders`
   * on the line). Each becomes one more row under the stock rows, carrying the
   * PO number, the quantity still to arrive and the delivery date.
   */
  incomingRows?: Array<any>;
  /** Needed to show the auto-fill toggle when there are no rows yet. */
  variantId?: number | null;
  onAutoFillChange?: (allowed: boolean) => void;
  /**
   * Render "we hold none" instead of nothing when there is no stock.
   *
   * The procurement cart leaves this off: it only makes a row expandable when
   * stock exists, so an empty panel is unreachable. The availability desk needs
   * it — 91% of the lines waiting there have no stock (that is WHY they are
   * waiting), and a silent `null` is indistinguishable from a broken feature.
   * "None in any warehouse" is a real answer to the desk's question.
   */
  showEmpty?: boolean;
  /**
   * The incoming PO line (mirror row id) currently chosen for this cart line, and the
   * callback a PO row's "Use this PO" button fires (null = clear). Both optional: the OMS desk
   * renders the panel read-only and passes neither.
   */
  selectedIncomingPoId?: number | null;
  onPickIncomingPo?: (row: any | null) => void;
}) {
  const stockRows = rows ?? [];
  const poRows = incomingRows ?? [];
  const resolvedVariantId =
    variantId ?? stockRows[0]?.variant_id ?? poRows[0]?.variant_id ?? null;
  const allowed = stockRows[0]?.auto_fill_allowed ?? null;
  const tone = allowed ? ALLOWED : BLOCKED;
  const incomingTotal = poRows.reduce((t: number, r: any) => t + Number(r?.quantity ?? 0), 0);

  // Stock first, then what is on order — one table so the columns line up.
  const dataSource: Array<PanelRow> = [
    ...stockRows.map((r: any) => ({ ...r, __kind: "stock" as const, __key: `stock-${r?.id}` })),
    ...poRows.map((r: any) => ({ ...r, __kind: "incoming_po" as const, __key: `po-${r?.id}` })),
  ];

  if (dataSource.length === 0) {
    if (!showEmpty) return null;
    return (
      <div
        style={{
          borderLeft: `3px solid ${ACCENT}`,
          background: "#fff",
          padding: "10px 14px",
          color: "#8c8c8c",
          fontSize: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span>No stock in any warehouse.</span>
        <AutoFillToggle variantId={resolvedVariantId} allowed={allowed} onChange={onAutoFillChange} />
      </div>
    );
  }

  const columns = [
    {
      key: "auto_fill",
      title: "Auto-fill",
      width: 140,
      render: (inv: PanelRow) => {
        if (isIncoming(inv)) {
          const picked =
            selectedIncomingPoId != null && Number(selectedIncomingPoId) === Number(inv?.id);
          return (
            <Space size={6}>
              <Tooltip title="Stock on order in Zoho Books; not in any warehouse yet, so it cannot auto-fill">
                <Tag icon={<FileTextOutlined />} style={{ margin: 0, fontWeight: 600 }}>
                  Incoming PO
                </Tag>
              </Tooltip>
              {onPickIncomingPo &&
                (picked ? (
                  <Tag
                    color="blue"
                    style={{ margin: 0 }}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      onPickIncomingPo(null);
                    }}
                  >
                    Selected
                  </Tag>
                ) : (
                  <Button
                    size="small"
                    type="link"
                    style={{ padding: 0, height: "auto" }}
                    onClick={() => onPickIncomingPo(inv)}
                  >
                    Use this PO
                  </Button>
                ))}
            </Space>
          );
        }
        const tone = rowTone(inv);
        return (
          <Tag
            style={{
              margin: 0,
              background: tone.bg,
              borderColor: tone.border,
              color: tone.fg,
              fontWeight: 600,
            }}
          >
            {inv?.auto_fill_allowed ? "✓ Allowed" : "Off"}
          </Tag>
        );
      },
    },
    {
      key: "warehouse",
      title: "Warehouse",
      width: 200,
      render: (inv: any) => (
        <Space size={6} align="center">
          <span style={{ fontWeight: 600, color: "#1f1f1f" }}>
            {inv?.warehouse_name || inv?.vendor_godown_name || "—"}
          </span>
        </Space>
      ),
    },
    {
      key: "vendor",
      title: "Vendor",
      width: 150,
      render: (inv: any) => (
        <Typography.Text
          style={{ color: "#595959" }}
          ellipsis={{ tooltip: inv?.vendor_name }}
        >
          {inv?.vendor_name || "—"}
        </Typography.Text>
      ),
    },
    {
      key: "batch",
      title: "Batch / PO No.",
      width: 130,
      render: (inv: PanelRow) =>
        isIncoming(inv) ? (
          <Tooltip
            title={[
              inv?.received_status_formatted || inv?.status_formatted,
              `ordered ${formatQty(inv?.quantity_ordered)}`,
              `received ${formatQty(inv?.quantity_received)}`,
              Number(inv?.quantity_in_transit ?? 0) > 0
                ? `in transit ${formatQty(inv?.quantity_in_transit)}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            <a
              href={inv?.zoho_purchase_order_id ? `${ZOHO_PO_URL}/${inv.zoho_purchase_order_id}` : undefined}
              target="_blank"
              rel="noreferrer"
              style={{ fontWeight: 600 }}
            >
              {inv?.purchase_order_number || "PO"}
            </a>
          </Tooltip>
        ) : inv?.batch_no ? (
          <Tag
            icon={<GoldOutlined />}
            style={{
              margin: 0,
              background: "#f6ffed",
              borderColor: "#b7eb8f",
              color: "#389e0d",
            }}
          >
            {inv.batch_no}
          </Tag>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      key: "quantity",
      title: <div style={{ textAlign: "right" }}>Qty</div>,
      width: 130,
      render: (inv: PanelRow) => {
        if (isIncoming(inv)) {
          // What is STILL TO ARRIVE on this PO line (ordered − received − cancelled).
          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
              <span style={{ fontWeight: 600, color: "#1f1f1f", fontVariantNumeric: "tabular-nums" }}>
                {formatQty(inv?.quantity)}
              </span>
              <span style={{ fontSize: 11, color: "#8c8c8c" }}>incoming</span>
            </div>
          );
        }
        const status = stockStatus(inv?.quantity);
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 6,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                color: "#1f1f1f",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatQty(inv?.quantity)}
            </span>
            <Tooltip title={status.label}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: status.dot,
                  display: "inline-block",
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: "last_updated",
      title: "Updated / Delivery",
      width: 170,
      render: (inv: PanelRow) => {
        if (isIncoming(inv)) return <IncomingDeliveryDate row={inv} />;
        const ts = inv?.last_updated_in_vendor_warehouse;
        if (!ts) return <span style={{ color: "#bfbfbf" }}>—</span>;
        return (
          <Tooltip title={moment(ts).format("DD MMM YYYY, hh:mm A")}>
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {moment(ts).fromNow()}
            </span>
          </Tooltip>
        );
      },
    },
    // Spacer: absorbs the remaining row width so the columns above stay
    // compact while the table still spans the full main-table row.
    {
      key: "spacer",
      title: "",
      render: () => null,
    },
  ];

  return (
    <div
      style={{
        borderLeft: `3px solid ${ACCENT}`,
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 14px",
          borderBottom: `1px solid ${tone.border}`,
          background: tone.bg,
          fontSize: 12,
          color: tone.fg,
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {stockRows.length > 0 ? (
            <>
              {tone.label} · {stockRows.length} warehouse row{stockRows.length === 1 ? "" : "s"} · total{" "}
              {formatQty(stockRows.reduce((t: number, r: any) => t + Number(r?.quantity ?? 0), 0))}
            </>
          ) : (
            <>No stock in any warehouse</>
          )}
          {poRows.length > 0 && (
            <>
              {" "}· {formatQty(incomingTotal)} incoming on {poRows.length} PO line
              {poRows.length === 1 ? "" : "s"}
            </>
          )}
          {!allowed && stockRows.length > 0 && (
            <span style={{ fontWeight: 400 }}> (Pls adjust in Zoho and enable the variant)</span>
          )}
        </span>
        <AutoFillToggle variantId={resolvedVariantId} allowed={allowed} onChange={onAutoFillChange} />
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="__key"
        pagination={false}
        size="small"
        onRow={(inv: PanelRow) => {
          if (isIncoming(inv)) {
            // Uncoloured while the delivery date is ahead; light red once it has passed.
            const overdue = !!inv?.is_overdue;
            return {
              style: overdue
                ? { background: INCOMING_OVERDUE.bg, boxShadow: `inset 3px 0 0 ${INCOMING_OVERDUE.border}` }
                : {},
              title: overdue ? "Incoming PO · delivery date has passed" : "Incoming PO",
            };
          }
          const tone = rowTone(inv);
          return {
            style: {
              background: tone.bg,
              boxShadow: `inset 3px 0 0 ${tone.border}`,
            },
            title: tone.label,
          };
        }}
      />
    </div>
  );
}

